import SlideTemplate from "./SlideTemplate";
import TabButton from "../components/TabButton";
import {resetScrollConfig} from "../helpers/tools";

export default {
  /**
   *
   * @returns {{intro: boolean}}
   */
  data() {
    return {
      intro: true,
      tab: 'a'
    }
  },

  methods: {
    /**
     *
     */
    handleContinue() {
      resetScrollConfig();
      this.intro = false;
    },

    /**
     *
     * @param tab
     * @returns {Function}
     */
    setTab(tab) {
      return () => {
        resetScrollConfig();
        this.tab = tab;
      }
    }
  },

  /**
   *
   * @param h
   * @returns {*}
   */
  render(h) {
    if (this.intro) {
      return (
        <SlideTemplate>
          <div class="p-6 py-16 lg:p-24 flex flex-col items-baseline justify-center min-h-full">

            <p class="text-3xl lg:text-5xl font-bold text-brand-blue-600">
              Case Study
            </p>

            <p class="lg:text-2xl mb-10 lg:mb-16">
              You have already seen the scenario below as part of an earlier activity. It is provided here for you to
              reference as needed during the simulation.
              <br/><br/>
              When you have finished reviewing, go to the Stakeholders view to learn more about the district and
              facilities where you will be responsible for implementing this change.
            </p>

            <button type="button" onClick={this.handleContinue}
                    class="lg:mb-5 rounded-full bg-brand-orange-700 text-white uppercase py-2 px-10 lg:text-xl hover:bg-orange-700 focus:outline-none transition-colors duration-100">
              Continue
            </button>
          </div>
        </SlideTemplate>
      );
    }

    const tabButtons = (
      <div class="p-2 pb-0 border-b border-brand-gray-800 sticky top-0"
           style="background: rgba(255, 255, 255, 0.9)">
        <ul class="flex items-stretch -mb-px text-sm lg:text-lg leading-tight select-none">
          <li class="w-1/3 flex">
            <TabButton handleClick={this.setTab('a')} active={this.tab === 'a'}>
              The Change
            </TabButton>
          </li>
          <li class="w-1/3 flex">
            <TabButton handleClick={this.setTab('b')} active={this.tab === 'b'}>
              Health &amp; EPI System
            </TabButton>
          </li>
          <li class="w-1/3 flex">
            <TabButton handleClick={this.setTab('c')} active={this.tab === 'c'}>
              Niand District
            </TabButton>
          </li>
        </ul>
      </div>
    );

    const helpText = (
      <p class="text-brand-gray-500 mb-3 leading-none">
        <strong class="mr-1">Case Study</strong>
        <small>(When you are finished reading the case, select Stakeholders from the top menu to continue.)</small>
      </p>
    );

    if (this.tab === 'a') {
      return (
        <SlideTemplate key="a">
          {tabButtons}
          <div class="p-6 lg:px-24">
            {helpText}

            <p class="leading-tight">
              National-level EPI management has recently decided to implement facility-level quality improvement efforts
              to ensure that all children are being reached with immunization services.
            </p>

            <div class="my-3">
              <strong class="mb-3">This program will consist of:</strong>

              <ul class="flex flex-col lg:flex-row lg:justify-between py-3 leading-tight text-base lg:-mr-24">
                <li class="lg:w-1/3 flex items-start py-6 lg:py-3">
                  <img class="w-2/5 lg:w-1/2" src="/img/changemanagement/diagram-quality-improvement.svg" alt=""/>
                  <p class="px-3">
                    <strong>Quality improvement teams</strong> at the facility level, empowered to iteratively solve
                    immunization-related problems, with the active input of community members.
                  </p>
                </li>
                <li class="lg:w-1/3 flex items-start py-6 lg:py-3">
                  <img class="w-2/5 lg:w-1/2 xl:w-2/5 px-5 lg:px-0" src="/img/changemanagement/diagram-microplans.svg" alt=""/>
                  <p class="px-3">
                    <strong>Data-driven micro-plans</strong> created and used at the facility level, by the quality
                    improvement teams.
                  </p>
                </li>
                <li class="lg:w-1/3 flex items-start py-6 lg:py-3">
                  <img class="w-2/5 lg:w-1/2" src="/img/changemanagement/diagram-supportive-supervision.svg" alt=""/>
                  <p class="px-3">
                    <strong>Supportive supervision</strong> by district managers to assist these efforts, with visits on
                    at least a quarterly basis.
                  </p>
                </li>
              </ul>
            </div>

            <p class="leading-tight">
              This will be a substantial change to this EPI management system, since most planning and problem solving is
              currently conducted at the district level. However, immunization coverage rates have stagnated in the past
              three years, and there is an acknowledged need to find more local solutions to specific challenges.
              <br/><br/>
              <strong>
                You have been tasked with implementing the change in several facilities in the Niand district. You can
                learn more about this district and the selected facilities in the Stakeholders view.
              </strong>
            </p>
          </div>
        </SlideTemplate>
      )
    } else if (this.tab === 'b') {
      return (
        <SlideTemplate key="b">
          {tabButtons}
          <div class="p-6 lg:px-24">
            {helpText}

            <p class="leading-tight">
              This country has made major improvements in its health system over the past decade. Trust in the health
              system is generally high, and national and regional leadership within the system is considered effective.
              Employees say they appreciate the open, supportive work culture.
              <br/><br/>
              Still, there are challenges. In some districts, dispersed populations and challenging terrains make it
              difficult to reach children with health services, including routine immunization.
              Increases in immunization coverage rates have stagnated in the past three years,
              likely due to these challenges. <strong>Key EPI roles within this health system:</strong>
            </p>

            <ul class="my-5 flex flex-wrap lg:flex-no-wrap justify-center lg:justify-between text-xs lg:text-sm">
              <li class="flex flex-col items-center mx-3 lg:mx-0 mb-3 lg:mb-0">
                <img src="/img/changemanagement/img-EPI-focal-point@2x.png" alt="" class="w-24 lg:w-32"/>
                Epi Focal Point
              </li>
              <li class="flex flex-col items-center mx-3 lg:mx-0 mb-3 lg:mb-0">
                <img src="/img/changemanagement/img-health-officer@2x.png" alt="" class="w-24 lg:w-32"/>
                Health Officer
              </li>
              <li class="flex flex-col items-center mx-3 lg:mx-0 mb-3 lg:mb-0">
                <img src="/img/changemanagement/img-manager@2x.png" alt="" class="w-24 lg:w-32"/>
                Manager
              </li>
              <li class="flex flex-col items-center mx-3 lg:mx-0 mb-3 lg:mb-0">
                <img src="/img/changemanagement/img-vaccinator@2x.png" alt="" class="w-24 lg:w-32"/>
                Vaccinator
              </li>
              <li class="flex flex-col items-center mx-3 lg:mx-0 mb-3 lg:mb-0">
                <img src="/img/changemanagement/img-community@2x.png" alt="" class="w-24 lg:w-32"/>
                Community
              </li>
            </ul>

            <p class="leading-tight">
              While the national and regional levels provide guidance and support, most of the planning for EPI in this
              system is conducted at the district level. Facility-level management views EPI as just a small part of
              their
              overall job, and generally relies on the directions of the district level when planning routine
              immunization
              and outreach sessions. Each facility has one or more vaccinators who provide immunization services in
              addition to their other roles.
            </p>
          </div>
        </SlideTemplate>
      )
    } else if (this.tab === 'c') {
      return (
        <SlideTemplate key="c">
          {tabButtons}
          <div class="p-6 lg:px-24">
            <div class="flex flex-col lg:flex-row">
              <div class="lg:w-6/12 lg:pr-12">
                {helpText}

                <p class="leading-tight">
                  Niand district is largely rural, with one larger city. Dispersed populations and mountainous terrains
                  mean that it can be challenging to reach all populations with health services. Twenty-six rural and
                  seven urban health facilities provide routine immunization services in this district.
                  <br/><br/>
                  The total population of district is estimated at 118,000. Last year, there were 4130 live births and
                  4033 surviving infants in this district.
                  <br/><br/>
                  Only 69% of infants received a third dose of pentavalent vaccine last year in this district,
                  compared to 83% in the country as a whole. Health facility staff turnover is high, almost 30% last
                  year.
                  <br/><br/>
                  You have been asked to lead the implementation of this program in two rural and one urban health
                  facilities, within this district.
                </p>
              </div>
              <div class="lg:w-6/12">
                <img src="/img/changemanagement/map1@2x.png" alt="" class="w-full"/>
              </div>
            </div>
          </div>
        </SlideTemplate>
      )
    }
    return null;
  }
}
